import { RecordChip } from 'components/record-chip/RecordChip';
import { FormattedDate } from 'components/misc/FormattedDate';
import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useIntl } from 'react-intl';
import { dateStringToDateObject } from 'utils/parseDate';
import type { FigureCell, FigureRowsColumn } from 'types/aiChatFigure';
import { useAiChatInternalLinkHandler } from '../../../aiChatInternalLinkContext';
import { formatRelativeTimeFromNow } from '../../../utils/relativeTime';
import { isModifiedClick } from '../isModifiedClick';
import { cellText, isMoneyCell, isNotSet, recordOf } from './figureRows';

const DAY = /^\d{4}-\d{2}-\d{2}$/;

interface Props {
    cell: FigureCell;
    column: FigureRowsColumn;
    /**
     * Where in a list's row the cell reads, as the boards draw it: the detail line reads a date as its year ("2025"),
     * the value at the end a date as the time since it ("9 months ago") and a percentage whole ("92 %").
     */
    listPart?: 'detail' | 'value';
    chipClassName?: string;
    /** A record chip without its native title, for a caller that shows a cut label itself. */
    hideChipTitle?: boolean;
    /** A record chip without its extended hit area, where chips wrap onto lines too close for it. */
    hitArea?: boolean;
    /** A number's or a percentage's decimals, its table column's, so the column reads in one form. */
    decimals?: number;
}

/**
 * A Figure cell drawn as what it is: a record as its chip, amounts and hours in the app's formats, a percentage of its
 * fraction, a date in the firm's date format, an empty value never entered as "Not set". A record chip opens its record in place, as a record link in the answer
 * does, and tells the surface first.
 */
export function FigureCellValue({ cell, column, listPart, chipClassName, hideChipTitle, hitArea, decimals }: Props) {
    const intl = useIntl();
    const onInternalLinkNavigate = useAiChatInternalLinkHandler();
    const record = recordOf(cell);

    if (isNotSet(cell, column)) {
        return <span className="text-foreground-tertiary">{intl.formatMessage({ id: 'ai-chat.figure.not-set' })}</span>;
    }
    if (record) {
        return (
            <RecordChip
                {...record}
                className={chipClassName}
                hideTitle={hideChipTitle}
                hitArea={hitArea}
                data-ai-chat-link="internal"
                onClick={(event) => {
                    if (!isModifiedClick(event)) {
                        onInternalLinkNavigate();
                    }
                }}
            />
        );
    }
    if (isMoneyCell(cell)) {
        return <MoneyDisplay amount={cell.amount} currency={cell.currency ?? undefined} />;
    }
    if (typeof cell === 'number' && Number.isFinite(cell)) {
        switch (column.type) {
            case 'hours':
                return <HoursDisplay value={cell} />;
            case 'percentage': {
                // The app's percent, a space before the sign: "38.2 %".
                const digits =
                    decimals === undefined
                        ? { maximumFractionDigits: listPart === 'value' ? 0 : 1 }
                        : { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
                return <>{`${intl.formatNumber(cell * 100, digits)}\u00a0%`}</>;
            }
            case 'number':
            case 'money':
                return <MoneyDisplay amount={cell} decimalScale={decimals ?? (Number.isInteger(cell) ? 0 : 2)} />;
            default:
                break;
        }
    }
    if (column.type === 'date' && typeof cell === 'string') {
        // A bare `yyyy-MM-dd` is a calendar day: local midnight, not UTC's, which is the day before west of UTC.
        const date = DAY.test(cell) ? dateStringToDateObject(cell) : new Date(cell);
        if (listPart === 'value' && !Number.isNaN(date.getTime())) {
            return <>{formatRelativeTimeFromNow(intl, date.getTime())}</>;
        }
        if (listPart === 'detail' && !Number.isNaN(date.getTime())) {
            return <>{date.getFullYear()}</>;
        }
        return <FormattedDate value={date} fallback={cell} />;
    }
    return <>{cellText(cell)}</>;
}
